import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Input, OptionalLabel } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import React from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { FieldError } from './field-error';
const MASK = '__h__';
function formatDigitsToMask(digits) {
    const d = digits.replace(/\D/g, '').slice(0, 4);
    const padded = d + '_'.repeat(4 - d.length);
    return `${padded.slice(0, 2)}h${padded.slice(2, 4)}`;
}
function getCursorPositionForDigitCount(digitCount) {
    // Positions: 0,1,2 = before/in HH, 3 = 'h' separator, 4,5 = mm
    if (digitCount <= 2)
        return digitCount;
    return digitCount + 1; // +1 to skip the 'h'
}
function isValidTime(digits) {
    if (digits.length !== 4)
        return false;
    const hours = Number.parseInt(digits.slice(0, 2), 10);
    const minutes = Number.parseInt(digits.slice(2, 4), 10);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}
// eslint-disable-next-line perfectionist/sort-modules
export default function TimeField({ label, optional = false }) {
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    const inputRef = React.useRef(null);
    // Derive display value from field state (stored as "HH:mm")
    const displayValue = field.state.value
        ? `${field.state.value.slice(0, 2)}h${field.state.value.slice(3, 5)}`
        : MASK;
    const [inputValue, setInputValue] = React.useState(displayValue);
    React.useEffect(() => {
        setInputValue(displayValue);
    }, [displayValue]);
    const tryParseAndSetTime = React.useCallback((digits) => {
        if (digits.length === 4 && isValidTime(digits)) {
            field.handleChange(`${digits.slice(0, 2)}:${digits.slice(2, 4)}`);
        }
        else if (digits.length === 0 && optional) {
            field.handleChange(null);
        }
    }, [field, optional]);
    return (_jsxs("div", { className: "flex flex-1 flex-col gap-2", children: [_jsx(OptionalLabel, { "aria-invalid": errors.length > 0, label: label, optional: optional }), _jsx(Input, { ref: inputRef, "aria-invalid": errors.length > 0, onBlur: field.handleBlur, onChange: (e) => {
                    const input = e.target;
                    const oldDigits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                    const newDigits = e.target.value.replace(/\D/g, '').replace(/_/g, '').slice(0, 4);
                    if (newDigits !== oldDigits) {
                        const masked = formatDigitsToMask(newDigits);
                        setInputValue(masked);
                        tryParseAndSetTime(newDigits);
                        const newCursor = getCursorPositionForDigitCount(newDigits.length);
                        requestAnimationFrame(() => {
                            input.setSelectionRange(newCursor, newCursor);
                        });
                    }
                }, onClick: (e) => {
                    const input = e.currentTarget;
                    const digits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                    const cursorPos = getCursorPositionForDigitCount(digits.length);
                    input.setSelectionRange(cursorPos, cursorPos);
                }, onFocus: (e) => {
                    const input = e.currentTarget;
                    const digits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                    const cursorPos = getCursorPositionForDigitCount(digits.length);
                    requestAnimationFrame(() => {
                        input.setSelectionRange(cursorPos, cursorPos);
                    });
                }, onKeyDown: (e) => {
                    const input = e.currentTarget;
                    if (e.key === 'Backspace') {
                        e.preventDefault();
                        const digits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                        if (digits.length > 0) {
                            const newDigits = digits.slice(0, -1);
                            const masked = formatDigitsToMask(newDigits);
                            setInputValue(masked);
                            tryParseAndSetTime(newDigits);
                            const newCursor = getCursorPositionForDigitCount(newDigits.length);
                            requestAnimationFrame(() => {
                                input.setSelectionRange(newCursor, newCursor);
                            });
                        }
                        return;
                    }
                    // Block Delete key - no effect in typewriter mode
                    if (e.key === 'Delete') {
                        e.preventDefault();
                        return;
                    }
                    // Block arrow keys - cursor is locked
                    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                        e.preventDefault();
                        return;
                    }
                    // Block non-digit keys (allow Tab, etc.)
                    if (e.key.length === 1 && !/\d/.test(e.key)) {
                        e.preventDefault();
                    }
                }, value: inputValue }), _jsx(FieldError, { errors: errors })] }));
}
